import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  Output,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import {
  CalendarOptions,
  CustomContentGenerator,
  DayHeaderContentArg,
  EventClickArg,
  EventContentArg,
  EventDropArg,
  EventHoveringArg,
  EventInput,
  EventMountArg,
} from '@fullcalendar/core';
import { DateClickArg, EventResizeDoneArg } from '@fullcalendar/interaction';
import { ResourceLabelContentArg } from '@fullcalendar/resource';
import {
  ConfigurationService,
  TimezoneService,
} from '@sc/data-access-configuration';
import {
  AgendaEvent,
  AppointmentStatusEnum,
  Dossier,
  DossierStateEnum,
  Fitter,
} from '@sc/data-access-models';
import { JsonObjectKey, StorageService } from '@sc/data-access-storage';
import { TranslatorService } from '@sc/data-access-translation';
import { FitterNameDisplayService } from '@sc/feature-contextual-display';
import {
  CalendarHiddenDays,
  CalendarMode,
  EventExtendedProps,
  FittersManagerService,
  Resource,
  ResourceExtendedProps,
  ResourcesManagerService,
  defaultHiddenDays,
} from '@sc/feature-fitter-shared-libs';
import { dayjs } from '@sc/utils';
import { ConfigType, Dayjs, OpUnitType } from 'dayjs';
import { interval } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { BaseAbstractDirective } from '../../base-abstract';
import { CalendarAction, PlanningPanelsStates } from '../../models';
import {
  PlanningCalendarService,
  PlanningSandboxService,
  PlanningTooltipMakerService,
} from '../../services';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { PlanningCalendarHeaderComponent } from './planning-calendar-header';

@Component({
  selector: 'sc-planning-calendar',
  styleUrls: ['./planning-calendar.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FitterNameDisplayService],
  template: `
    <div class="container">
      <sc-planning-calendar-header
        [date]="currentDate"
        [basketOpen]="panelsStates.isRightSideNavOpened"
        [formOpen]="panelsStates.isAppointmentFormOpened"
        [mode]="calendarMode"
        [filterOpen]="panelsStates.isLeftSideNavOpened"
        [isLandscapeView]="isLandscapeView"
        (calendarEvent)="calendarAction($event)"
        (datePickerEvent)="datePickerAction($event)"
        [class.isFiltersSidenavOpened]="panelsStates.isLeftSideNavOpened"
        [class.isBasketSidenavOpened]="panelsStates.isRightSideNavOpened"
      ></sc-planning-calendar-header>
      <full-calendar
        [class.display]="displayCalendar()"
        #calendar
        [options]="calendarOptions"
      ></full-calendar>
      <div *ngIf="!displayCalendar()" class="no-calendar-container">
        <div class="no-calendar-text">
          <p>{{ 'PlanningAgenda.noCalendarHintTitle' | translate }}</p>
          <p class="no-calendar-body">
            {{ 'PlanningAgenda.noCalendarHintBody' | translate }}
          </p>
        </div>
      </div>
    </div>
  `,
  imports: [
    PlanningCalendarHeaderComponent,
    FullCalendarModule,
    NgIf,
    TranslateModule,
  ],
})
export class PlanningCalendarComponent
  extends BaseAbstractDirective
  implements AfterViewInit
{
  @Input()
  public set events(events: EventInput[]) {
    if (events?.length) {
      this.calendarComponent.getApi()?.pauseRendering();
      this.calendarComponent.getApi()?.removeAllEvents();
      this.calendarComponent.getApi()?.removeAllEventSources();

      if (this._resources) {
        const resourceNotRemplacement = this._resources.map(
          (resource) => resource.id
        );

        const eventsToAdd: EventInput[] = [];

        events.forEach((event: EventInput) => {
          const computedEvent: EventInput = {
            ...event,
            constraint: {
              resourceIds: resourceNotRemplacement,
            },
            editable:
              event.extendedProps?.dossier?.state !=
                DossierStateEnum.CANCELLED &&
              event.extendedProps?.dossier?.appointment?.status !=
                AppointmentStatusEnum.COMPLETED,
          };
          if (event?.extendedProps?.display) {
            eventsToAdd.push(computedEvent);
          }
        });

        this.calendarComponent.getApi()?.addEventSource(eventsToAdd);
        this.setSlotTimes();
      }
      this.calendarComponent.getApi()?.resumeRendering();
    }
  }
  @Input()
  public set resources(resources: Resource[]) {
    this._resources = resources ?? [];
    this.applyResourcesDisplayed(resources);
  }

  @Input() set basketEventDay(value: string | Date) {
    if (value) {
      this.currentDate = value;
      this.calendarComponent.getApi().gotoDate(value);
      this.loadEventsInStore();
    }
  }

  @Input() panelsStates: PlanningPanelsStates;
  @Input() isLandscapeView: boolean;

  @Input() fitters: Fitter[];

  @Output() toggleFiltersPanel = new EventEmitter<boolean>();
  @Output() toggleBasketPanel = new EventEmitter<boolean>();
  @Output() handleDateClickEvent = new EventEmitter<DateClickArg>();
  @Output() handleMoveEvent = new EventEmitter<{
    dossier: Dossier;
    startTime: string | Date;
    endTime: string | Date;
    timezone: string;
    newResource?: ResourceExtendedProps;
    oldResource?: ResourceExtendedProps;
    resizeResource?: ResourceExtendedProps;
  }>();
  @Output() handleEventClickEvent = new EventEmitter<EventClickArg>();

  @Output() handleEventMobileClickEvent = new EventEmitter<EventClickArg>();
  @Output() handleNewEventClickEvent = new EventEmitter<void>();
  @Output() handleNewInterventionClickEvent = new EventEmitter<void>();
  @Output() handleNewExceptionalClosingClickEvent = new EventEmitter<void>();
  @Output() handleUnavailabilityClickEvent = new EventEmitter<void>();
  @Output() changeCalendarDay = new EventEmitter<Date>();

  availableDuration = [5, 7.5, 10, 15, 30, 1];

  defaultSlotMinTime = '07:00:00';
  defaultSlotMaxTime = '20:00:00';

  calendarMode: CalendarMode = 'resourceTimeGridDay';
  hiddenDays: CalendarHiddenDays = this.storage.getItem<
    JsonObjectKey,
    CalendarHiddenDays
  >('calendar-hidden-days', localStorage, { ...defaultHiddenDays });

  nowIndicatorTop: string;

  @ViewChild('calendar', { static: true })
  public calendarComponent: FullCalendarComponent;

  private _resources: Resource[];
  currentDate: Date | string;
  calendarOptions: CalendarOptions = {
    ...this.planningCalendarService.defaultConf(this.calendarMode),
    dayHeaderContent: (info: DayHeaderContentArg) => this.applyDayStyle(info),
    eventContent: (info: EventContentArg) => this.applyEventContent(info),
    eventClick: (info) => this.handleEventClick(info),
    eventMouseEnter: (info: EventHoveringArg) => this.handleEventDidMount(info),
    eventResize: (info: EventResizeDoneArg) => this.handleEventResize(info),
    eventDrop: (info: EventDropArg) => this.handleEventDrop(info),
    eventDidMount: (info: EventMountArg) => {
      // Needed to force linear-gradient on background event
      info.el.style.background = info.event.backgroundColor;
    },
    dateClick: (date: DateClickArg) => this.handleDateClick(date),
    dayHeaderClassNames: (params) => {
      const { isFirstDay, isLastDay } = this.isExtremeDay(params.date);

      const resultClasses: string[] = [];
      if (isFirstDay) {
        resultClasses.push('first-week-day');
      }
      if (isLastDay) {
        resultClasses.push('last-week-day');
      }

      return resultClasses;
    },
    resourceLabelContent: (resource: ResourceLabelContentArg) => {
      return this.resourceLabelContent(resource);
    },
    windowResize: () => {
      this.planningCalendarService.waitForNextCalendarResize().subscribe(() => {
        this.calendarComponent.getApi()?.render();
        this.applyHeaderStyle();
      });
    },
    dayCellClassNames: (resource) => {
      if (resource.resource?._resource?.extendedProps.isLast) {
        return 'isCellLast';
      }
      return '';
    },
    slotLaneClassNames(hookProps) {
      if (hookProps.date.getMinutes() === 30) {
        return 'isHalfPastHour';
      }
      return '';
    },
    slotLabelClassNames(hookProps) {
      return 'isLabel';
    },
    loading: (isLoading) => {
      if (!isLoading) {
        // Triggered just after the loading is done (events not included)
        this.planningCalendarService
          .waitForNextCalendarResize('', 100, 1) // We only need to wait a little, not 1s
          .subscribe(() => {
            // Offsets of headers are present on load while no actions of the following list are performed :
            // - Inspecting element through F12
            // - Calling some of full calendar API methods (but not render, why ??????)
            // So we first call next and prev (invisible change) to remove header offset, then render and apply headers style
            this.calendarComponent.getApi()?.next();
            this.calendarComponent.getApi()?.prev();
            this.calendarComponent.getApi()?.render();
            this.applyHeaderStyle();
          });
      }
    },
  };

  constructor(
    public injector: Injector,
    private readonly planningSb: PlanningSandboxService,
    private readonly translator: TranslatorService,
    private readonly planningCalendarService: PlanningCalendarService,
    private readonly resManagerService: ResourcesManagerService,
    private readonly fitterManagerService: FittersManagerService,
    private readonly planningTooltipService: PlanningTooltipMakerService,
    private readonly configurationService: ConfigurationService,
    private readonly storage: StorageService,
    private readonly renderer: Renderer2,
    private readonly fitterNameDisplayService: FitterNameDisplayService,
    private readonly timezoneService: TimezoneService
  ) {
    super(injector);
  }

  ngAfterViewInit(): void {
    this.subs.rendererPlanning = this.planningSb.rerenderPlanning$.subscribe(
      () => {
        this.calendarComponent.getApi()?.render();
        this.applyHeaderStyle();
        this.calendarOptions.slotMaxTime = this.defaultSlotMaxTime;
        this.calendarOptions.slotMinTime = this.defaultSlotMinTime;
        this.setNowIndicator();
      }
    );

    this.calendarMode = this.isLandscapeView
      ? 'resourceTimeGridDay'
      : this.getStoredCalendarView();
    this.applyCalendarView();

    const savedDayWidth = this.storage.getItem('calendar-day-width');
    if (savedDayWidth) {
      this.calendarComponent.getApi()?.setOption('dayMinWidth', savedDayWidth);
    }
  }

  public displayCalendar() {
    return this._resources.filter((res) => !!res.display)?.length > 0;
  }

  // Function needed for some cases where calendarComponent.getApi() is undefined when calling for applyResourcesDisplayed
  private getCalendarApi$() {
    return interval(100).pipe(
      map(() => this.calendarComponent.getApi()),
      filter((calendarApi) => !!calendarApi),
      take(1)
    );
  }

  public calendarAction = (calendarAction: CalendarAction): void => {
    const currentDuration = +(
      this.calendarComponent.getApi().getOption('slotDuration') as {
        minute: number;
      }
    ).minute;

    const currentZoomW = this.calendarComponent
      ?.getApi()
      ?.getOption('dayMinWidth');

    const isCalendarWiderThanScreen =
      document.querySelector('.fc-timegrid-body')?.clientWidth >
      window.innerWidth;

    switch (calendarAction) {
      case CalendarAction.TOGGLE_FILTER:
        this.toggleFiltersPanel.emit();
        break;
      case CalendarAction.TOGGLE_BASKET:
        this.toggleBasketPanel.emit();
        break;
      case CalendarAction.PREV: {
        this.calendarComponent.getApi()?.prev();
        this.loadEventsInStore();
        break;
      }
      case CalendarAction.TODAY:
        this.calendarComponent.getApi()?.today();
        document
          .querySelector("th[data-date='" + dayjs().format('YYYY-MM-DD') + "']")
          .scrollIntoView({ inline: 'center' });
        this.loadEventsInStore();
        break;
      case CalendarAction.NEXT:
        this.calendarComponent.getApi()?.next();
        this.loadEventsInStore();
        break;
      case CalendarAction.DAYS:
        this.calendarMode = 'resourceTimeGridDay';
        this.applyCalendarView();
        break;
      case CalendarAction.WORK_WEEKS:
        this.calendarMode = 'resourceTimeGridWeek';
        this.applyCalendarView();
        break;
      case CalendarAction.ZOOM_OUT_H:
        // Deal with that thanks to the tab that give available interval if possible
        if (currentDuration < 30) {
          const indexCurrentDuration = this.availableDuration.findIndex(
            (aD) => aD === currentDuration
          );
          if (this.availableDuration[indexCurrentDuration + 1]) {
            this.calendarComponent.getApi().setOption('slotDuration', {
              minute: this.availableDuration[indexCurrentDuration + 1],
            });
            this.storage.setItem(
              'slotDuration',
              this.availableDuration[indexCurrentDuration + 1]
            );
          }
          this.setHeight();
        }

        break;
      case CalendarAction.ZOOM_IN_H:
        // Deal with that thanks to the tab that give available interval if possible
        if (currentDuration > 5) {
          const indexCurrentDuration = this.availableDuration.findIndex(
            (aD) => aD === currentDuration
          );
          if (this.availableDuration[indexCurrentDuration - 1]) {
            this.calendarComponent.getApi().setOption('slotDuration', {
              minute: this.availableDuration[indexCurrentDuration - 1],
            });
            this.storage.setItem(
              'slotDuration',
              this.availableDuration[indexCurrentDuration - 1]
            );
          }
          this.setHeight();
        }
        break;
      case CalendarAction.ZOOM_OUT_W:
        if (isCalendarWiderThanScreen) {
          this.calendarComponent
            .getApi()
            ?.setOption('dayMinWidth', Math.max(10, currentZoomW - 15));
          this.storage.setItem(
            'calendar-day-width',
            this.calendarComponent.getApi()?.getOption('dayMinWidth')
          );
        }
        break;
      case CalendarAction.ZOOM_IN_W:
        this.calendarComponent
          .getApi()
          ?.setOption('dayMinWidth', Math.min(500, currentZoomW + 15));
        this.storage.setItem(
          'calendar-day-width',
          this.calendarComponent.getApi()?.getOption('dayMinWidth')
        );
        break;
      case CalendarAction.EVENT:
        this.handleNewEventClickEvent.emit();
        break;
      case CalendarAction.INTERVENTION:
        this.handleNewInterventionClickEvent.emit();
        break;
      case CalendarAction.EXCEPTIONAL_CLOSING:
        this.handleNewExceptionalClosingClickEvent.emit();
        break;
      case CalendarAction.UNAVAILABILITY:
        this.handleUnavailabilityClickEvent.emit();
        break;
    }
    this.currentDate = this.calendarComponent.getApi()?.getDate();
    this.applyHeaderStyle();
    this.setNowIndicator();
  };

  public datePickerAction = (date: Date): void => {
    this.calendarComponent.getApi()?.gotoDate(date);
    this.loadEventsInStore();
    this.currentDate = this.calendarComponent.getApi()?.getDate();
    this.applyHeaderStyle();
    this.setNowIndicator();
  };
  private setHeight() {
    this.calendarComponent
      .getApi()
      .el.querySelector('.fc-timegrid-cols')
      .setAttribute(
        'style',
        `height: ${
          this.calendarComponent.getApi().el.querySelector('.fc-timegrid-slots')
            .clientHeight
        }px`
      );
  }

  applyCalendarView() {
    this.storeCalendarView(this.calendarMode);
    this.setSlotTimes();
    this.calendarComponent.getApi()?.changeView(this.calendarMode);
    this.hideDays();
  }

  hideDays() {
    // 0 = sunday, 1 = monday, ...
    // true = hide, false = show, undefined = keep as it is
    if (this.calendarMode === 'resourceTimeGridDay') {
      this.calendarComponent.getApi()?.setOption('hiddenDays', []);
      return;
    }

    const daysToHide = Object.keys(this.hiddenDays)
      .filter((k) => this.hiddenDays[k] === true)
      .map((k) => +k);

    this.calendarComponent.getApi()?.setOption('hiddenDays', daysToHide);
  }

  getStoredCalendarView(): CalendarMode {
    if (Object.values(this.hiddenDays).filter((hidden) => !hidden).length < 2) {
      return 'resourceTimeGridDay';
    }
    return this.storage.getItem(
      'agenda-calendar-mode',
      localStorage,
      'resourceTimeGridDay'
    ) as CalendarMode;
  }

  storeCalendarView(view: CalendarMode) {
    this.storage.setItem('agenda-calendar-mode', view);
  }

  isExtremeDay(date: ConfigType) {
    const dayDate = dayjs(date);
    const dayOfWeek = dayDate.day();

    const hiddenDays =
      this.calendarComponent.getApi()?.getOption('hiddenDays') || [];
    const allShownDays = [0, 1, 2, 3, 4, 5, 6].filter(
      (day) => !hiddenDays.includes(day)
    );
    const firstDayOfWeek =
      this.calendarComponent.getApi()?.getOption('firstDay') ?? 0;

    allShownDays.sort((dayA, dayB) => {
      const diffA = dayA - firstDayOfWeek;
      const diffB = dayB - firstDayOfWeek;

      if ((diffA >= 0 && diffB >= 0) || (diffA < 0 && diffB < 0)) {
        return diffA - diffB;
      } else if (diffA >= 0) {
        return -1;
      } else {
        return 1;
      }
    });

    const isFirstDay = dayOfWeek === allShownDays[0];
    const isLastDay = dayOfWeek === allShownDays[allShownDays.length - 1];

    return {
      isFirstDay,
      isLastDay,
    };
  }

  resourceLabelContent(
    arg: ResourceLabelContentArg
  ): CustomContentGenerator<ResourceLabelContentArg> {
    const extendedProps = arg.resource.extendedProps as ResourceExtendedProps;

    if (this.calendarComponent.getApi()?.view.type === 'resourceTimeGridDay') {
      if (extendedProps.isFirst) {
        return {
          html: `${this.buildTopHeader(arg)} ${this.buildBottomHeader(arg)}`,
        };
      } else {
        return {
          html: `${this.buildBottomHeader(arg)}`,
        };
      }
    } else {
      if (extendedProps.isFirst) {
        return {
          html: `${this.buildTopHeader(arg)} ${this.buildBottomHeader(arg)}`,
        };
      } else {
        return {
          html: `${this.buildBottomHeader(arg)}`,
        };
      }
    }
  }

  private buildTopHeader({ resource, date }: ResourceLabelContentArg): string {
    const extendedProps = resource.extendedProps as ResourceExtendedProps;

    let border = '';
    if (extendedProps.isGeneralFirst) {
      border += 'isGeneralFirst ';
    } else if (extendedProps.isGeneralLast) {
      border += 'isGeneralLast ';
    }
    const { isFirstDay, isLastDay } = this.isExtremeDay(date);
    if (isFirstDay) {
      border += 'fitter-first-week-day ';
    }
    if (isLastDay) {
      border += 'fitter-last-week-day ';
    }
    return `
      <div class="fitter-header ${border}" data-fitter-id="${
      extendedProps.fitterId
    }">
        <div class="fitter-brand">${this.fitterNameDisplayService.getInstantFitterNameTranslation(
          { id: extendedProps.fitterId, city: extendedProps.city },
          undefined,
          { ...extendedProps }
        )}</div>
        <div class="fitter-comment">
          <span class="fitter-comment-no-edit">${
            extendedProps.fitterComment
          }</span>
          <span class="fitter-comment-edit">
            <input class="fitter-comment-input" type="text" value="${
              extendedProps.fitterComment
            }" />
            <span class="fitter-comment-validate-btn"></span>
            <span class="fitter-comment-cancel-btn"></span>
          </span>
        </div>
      </div>
    `;
  }

  private buildBottomHeader({ resource, date }: ResourceLabelContentArg) {
    const extendedProps = resource.extendedProps as ResourceExtendedProps;

    const classesToAdd: string[] = [];
    const { isFirstDay, isLastDay } = this.isExtremeDay(date);
    const isGeneralFirst = !!extendedProps.isGeneralFirst;
    const isGeneralLast = !!extendedProps.isGeneralLast;
    if (extendedProps.isFirst) {
      classesToAdd.push('is-first');

      if (!isGeneralFirst) {
        classesToAdd.push('isHeaderFirst');
      }
    }
    if (isGeneralFirst) {
      classesToAdd.push('isGeneralFirst');
    }

    if (extendedProps.isLast) {
      classesToAdd.push('is-last');

      if (!isGeneralLast) {
        classesToAdd.push('isHeaderLast');
      }
    }
    if (isGeneralLast) {
      classesToAdd.push('isGeneralLast');
    }

    if (isFirstDay) {
      classesToAdd.push('header-first-week-day');
    }
    if (isLastDay) {
      classesToAdd.push('header-last-week-day');
    }

    let bottomHeaderTitle = this.translator.getInstantTranslation(
      `PlanningAgenda.resources.${extendedProps.type}`
    );

    if (extendedProps.type === 'TECHNICIAN') {
      if (this.resManagerService.isDefaultTechnician(extendedProps.resId)) {
        bottomHeaderTitle =
          this.fitters?.find((fitter) => fitter?.id === extendedProps?.fitterId)
            ?.filterLabels?.technicianLabel ??
          this.translator.getInstantTranslation(
            'PlanningAgenda.resources.DEFAULT_TECHNICIAN'
          );
      } else {
        bottomHeaderTitle = this.resManagerService.getResourceTechnician(
          extendedProps.resId
        );
      }
    }

    if (extendedProps.type === 'INTERVENTION_VEHICLE') {
      if (
        this.resManagerService.isDefaultInterventionVehicle(extendedProps.resId)
      ) {
        bottomHeaderTitle =
          this.fitters?.find((fitter) => fitter?.id === extendedProps?.fitterId)
            ?.filterLabels?.interventionVehicleLabel ??
          this.translator.getInstantTranslation(
            'PlanningAgenda.resources.DEFAULT_INTERVENTION_VEHICLE'
          );
      } else {
        bottomHeaderTitle =
          this.resManagerService.getResourceInterventionVehicle(
            extendedProps.resId
          );
      }
    }

    if (extendedProps.type === 'RV_DETAILED') {
      if (this.resManagerService.isDefaultRV(extendedProps.resId)) {
        bottomHeaderTitle =
          this.fitters?.find((fitter) => fitter?.id === extendedProps?.fitterId)
            ?.filterLabels?.replacementVehicleLabel ??
          this.translator.getInstantTranslation(
            'PlanningAgenda.resources.DEFAULT_RV'
          );
      } else {
        bottomHeaderTitle = this.resManagerService
          .getResourceRV(extendedProps.resId)
          ?.split('##')[1];
      }
    }

    return `
      <div class="resource-header-title ${classesToAdd.join(' ')}">
        <div class="bottom-header">
          ${bottomHeaderTitle}
        </div>
      </div>`;
  }

  applyResourcesDisplayed(resources: Resource[]) {
    // NOTE - the resources list should be sorted before entering this function

    this.getCalendarApi$().subscribe((calendarApi) => {
      calendarApi?.pauseRendering();
      const displayedRes = resources?.filter((res) => res.display) || [];

      displayedRes.forEach((resource, resourceIndex) => {
        // isFirst corresponds to the first resource (to the left) of a fitter
        // isLast corresponds to the last resource (to the right) of a fitter
        // isGeneralFirst corresponds to the first resource of the first fitter of the day
        // isGeneralLast corresponds to the last resource of the last fitter of the day
        // isFirst && not isGeneralFirst || between days corresponds to the first resource of each fitters except the first one
        // isLast && not isGeneralLast || between days corresponds to the last resource of each fitters except the last one

        const isFirst =
          displayedRes.findIndex(
            (otherRes, otherResIndex) =>
              otherRes.fitterId === resource.fitterId &&
              otherResIndex < resourceIndex
          ) === -1;
        const isLast =
          displayedRes.findIndex(
            (otherRes, otherResIndex) =>
              otherRes.fitterId === resource.fitterId &&
              otherResIndex > resourceIndex
          ) === -1;
        const isGeneralFirst =
          displayedRes.findIndex(
            (otherRes, otherResIndex) =>
              otherRes.fitterId !== resource.fitterId &&
              otherResIndex < resourceIndex
          ) === -1 && isFirst;
        const isGeneralLast =
          displayedRes.findIndex(
            (otherRes, otherResIndex) =>
              otherRes.fitterId !== resource.fitterId &&
              otherResIndex > resourceIndex
          ) === -1 && isLast;

        const extendedProps: ResourceExtendedProps = {
          resId: resource.id,
          type: resource?.type,
          fitterId: resource.fitterId,
          brand: resource.brand,
          city: resource.city,
          isGeneralFirst: isGeneralFirst,
          isGeneralLast: isGeneralLast,
          isFirst: isFirst,
          isLast: isLast,
          timezone: resource.timezone,
          fitterComment: resource.fitterComment,
        };

        calendarApi?.addResource({
          id: resource.id,
          title: `
              ${resource.brand}
              ${this.translator.getInstantTranslation(
                `PlanningFilters.filters.${resource.type}`
              )}`,
          businessHours: resource.businessHours,
          extendedProps: extendedProps,
          order: resourceIndex,
        });
      });
      const hiddenRes = resources?.filter((res) => !res.display) || [];
      const currentCalRes = calendarApi.getResources();
      hiddenRes.forEach((res) => {
        const foundRes = currentCalRes.find((calRes) => calRes.id === res.id);
        if (foundRes) {
          foundRes.remove();
        }
      });

      calendarApi?.getResources()?.forEach((calendarRes) => {
        if (
          resources.findIndex(
            (wantedRes) => wantedRes.id === calendarRes.id
          ) === -1
        ) {
          calendarRes.remove();
        }
      });

      this.applyHeaderStyle();
      this.setSlotTimes();
      calendarApi?.resumeRendering();
    });
  }

  applyHeaderStyle() {
    const displayedResourcesByFitter: {
      [key: string]: Resource[];
    } = this._resources
      .filter((res) => res.display)
      .reduce((acc, res) => {
        if (acc[res.fitterId]) {
          acc[res.fitterId].push(res);
        } else {
          acc[res.fitterId] = [res];
        }
        return acc;
      }, {});

    if (this.calendarComponent.getApi()?.view.type === 'resourceTimeGridDay') {
      this.applyResourcesHeaderWidthDay(displayedResourcesByFitter);
    } else if (
      this.calendarComponent.getApi()?.view.type === 'resourceTimeGridWeek'
    ) {
      this.applyResourcesHeaderWidthWeek(displayedResourcesByFitter);
    }
  }

  applyResourcesHeaderWidthDay(displayedResourcesByFitter: {
    [key: string]: Resource[];
  }) {
    for (const fitter in displayedResourcesByFitter) {
      const elementHeaderFitterType: HTMLElement = document.querySelector(
        `[data-fitter-id='${fitter}']`
      );
      if (elementHeaderFitterType) {
        elementHeaderFitterType.style.width = `${
          displayedResourcesByFitter[fitter].length * 100
        }%`;

        this.addFitterCommentBar(elementHeaderFitterType, fitter);
      }
    }
  }

  addFitterCommentBar(elementHeaderFitterType: HTMLElement, fitterId: string) {
    const fitterCommentContainer = this.findChildElement(
      elementHeaderFitterType,
      'fitter-comment'
    );

    const fitterCommentNoEditEl = this.findChildElement(
      fitterCommentContainer,
      'fitter-comment-no-edit'
    );
    const fitterCommentEditEl = this.findChildElement(
      fitterCommentContainer,
      'fitter-comment-edit'
    );
    const fitterCommentInput = this.findChildElement(
      fitterCommentEditEl,
      'fitter-comment-input'
    ) as HTMLInputElement;
    const fitterCommentValidateBtn = this.findChildElement(
      fitterCommentEditEl,
      'fitter-comment-validate-btn'
    );
    const fitterCommentCancelBtn = this.findChildElement(
      fitterCommentEditEl,
      'fitter-comment-cancel-btn'
    );

    fitterCommentContainer.addEventListener('click', () => {
      if (!fitterCommentContainer.classList.contains('edit-mode')) {
        fitterCommentContainer.classList.add('edit-mode');
      }
    });

    fitterCommentValidateBtn.addEventListener('click', (event) => {
      if (fitterCommentContainer.classList.contains('edit-mode')) {
        event.stopPropagation();

        this.planningSb.fitters$.pipe(take(1)).subscribe((fitters) => {
          const foundFitter = fitters.find((f) => f.id === fitterId);

          const originalFitterComment =
            this.planningCalendarService.getOwnFitterComment(foundFitter);

          if (originalFitterComment !== fitterCommentInput.value) {
            this.planningSb
              .updateFitterComment(fitterId, fitterCommentInput.value)
              .subscribe(
                () => {
                  fitterCommentNoEditEl.innerHTML = fitterCommentInput.value;
                  fitterCommentContainer.classList.remove('edit-mode');
                },
                () => {
                  fitterCommentInput.value = fitterCommentNoEditEl.innerHTML;
                  fitterCommentContainer.classList.remove('edit-mode');
                }
              );
          } else {
            fitterCommentContainer.classList.remove('edit-mode');
          }
        });
      }
    });

    fitterCommentCancelBtn.addEventListener('click', (event) => {
      if (fitterCommentContainer.classList.contains('edit-mode')) {
        event.stopPropagation();
        this.planningSb.fitters$.pipe(take(1)).subscribe((fitters) => {
          const foundFitter = fitters.find((f) => f.id === fitterId);

          const originalFitterComment =
            this.planningCalendarService.getOwnFitterComment(foundFitter);

          fitterCommentInput.value = originalFitterComment;
          fitterCommentContainer.classList.remove('edit-mode');
          fitterCommentNoEditEl.innerHTML = originalFitterComment;
        });
      }
    });
  }

  findChildElement(el: Element, classToFind: string): Element {
    let result: Element;
    for (let i = 0; i < el.children.length; i++) {
      const currentChild = el.children.item(i);
      if (currentChild.classList.contains(classToFind)) {
        result = currentChild;
        break;
      }
    }

    return result;
  }

  applyResourcesHeaderWidthWeek(displayedResourcesByFitter: {
    [key: string]: Resource[];
  }) {
    let elementHeaderAllFitterWidth = 0;
    for (const fitter in displayedResourcesByFitter) {
      let elementHeaderFitterWidth = 0;

      displayedResourcesByFitter[fitter].forEach((res) => {
        const identifier = `td[role='gridcell'][data-resource-id="${res.id}"]`;
        const elementHeaderType: HTMLElement =
          document.querySelector(identifier);

        elementHeaderFitterWidth += elementHeaderType?.clientWidth || 0;
      });

      const elementHeaderFitterTypes: NodeListOf<HTMLDivElement> =
        document.querySelectorAll(`[data-fitter-id='${fitter}']`);

      for (let i = 0; i < elementHeaderFitterTypes.length; i++) {
        elementHeaderFitterTypes[i].style.width = `calc(${
          displayedResourcesByFitter[fitter].length * 100
        }% + ${
          // We add some pixels since there is always an offset of 0.8px between the th (ex: 150px)
          // and div that serves as the base of the percentage calculation (ex: 149.2px)
          Math.floor(displayedResourcesByFitter[fitter].length * 0.8)
        }px)`;
      }
      elementHeaderAllFitterWidth += elementHeaderFitterWidth;
    }

    const elementHeaderAllFitters: NodeListOf<HTMLTableSectionElement> =
      document.querySelectorAll(`th[role='columnheader'].fc-day`);
    for (let i = 0; i < elementHeaderAllFitters.length; i++) {
      elementHeaderAllFitters[i].style.width = `${
        elementHeaderAllFitterWidth + 1
      }px`;
    }
  }

  applyDayStyle = (info: DayHeaderContentArg) => {
    const rawDate = info.date;
    const timezone = this.timezoneService.getFitterListTimezone(
      ...this.fitters
    );
    const offsetedDate =
      this.planningCalendarService.computeTimeOffsetOffCalendar(
        rawDate,
        timezone
      );
    return this.calendarMode === 'resourceTimeGridWeek'
      ? {
          html: `
      <div class="header-date">
        <div class="day" >
          ${dayjs(offsetedDate).tz(timezone).format('dddd')}
        </div>
        <div class="day-month ${info.isToday ? 'is-today' : ''}">
          ${dayjs(offsetedDate).tz(timezone).format('D')}
        </div>
        <div class="day" >
          ${dayjs(offsetedDate).tz(timezone).format('MMMM')}
        </div>
      </div>
    `,
        }
      : {
          html: `
      <div class="header-date">
        <div class="day" >
          ${dayjs(offsetedDate).tz(timezone).format('dddd')}
        </div>
        <div class="day-month ${info.isToday ? 'is-today' : ''}">
          ${dayjs(offsetedDate).tz(timezone).format('D')}
        </div>
      </div>
    `,
        };
  };

  applyEventContent(info: EventContentArg) {
    const extendedProps = info.event.extendedProps as EventExtendedProps;

    if (this.resManagerService.isDossierEventExtendedProps(extendedProps)) {
      const dossier: Dossier = extendedProps.dossier;
      const fitter: Fitter = extendedProps.fitter;
      return {
        html: this.generateDossierInformationContent(dossier, fitter),
      };
    } else if (
      this.resManagerService.isGenericEventExtendedProps(extendedProps)
    ) {
      const event: AgendaEvent = extendedProps.event;
      return {
        html: this.generateCustomEventInformationContent(event),
      };
    } else {
      return '';
    }
  }

  generateDossierInformationContent(dossier: Dossier, fitter: Fitter): string {
    return this.planningCalendarService.generateContentForDossier(
      dossier,
      fitter,
      this.configurationService
    );
  }

  generateCustomEventInformationContent(event: AgendaEvent): string {
    return this.planningCalendarService.generateContentForCustomEvent(event);
  }

  handleDateClick(date: DateClickArg): void {
    if (this.canMakeActionOnCalendar()) {
      this.handleDateClickEvent.emit(date);
    }
  }

  onTooltipClick(event: EventHoveringArg) {
    //wait until tooltip is added in DOM
    setTimeout(() => {
      const tooltip = (<HTMLElement>document.body).querySelector(
        '.tooltip-agenda'
      );

      if (tooltip) {
        this.renderer?.listen(tooltip, 'touchstart', (e) => {
          e?.preventDefault();
          //Wait until tooltip appears
          setTimeout(() => {
            if (this.isLandscapeView) {
              this.handleEventMobileClickEvent.emit(event);
            } else if (this.canMakeActionOnCalendar()) {
              this.handleEventClickEvent.emit(event);
            }
          }, 500);
        });
      }
    }, 500);
  }

  handleEventDidMount(info: EventHoveringArg): void {
    const isTouchClick =
      this.isLandscapeView || info?.jsEvent['pointerType'] === 'touch';

    this.planningTooltipService.display(info, isTouchClick);
    if (isTouchClick) {
      this.onTooltipClick(info);
    }
  }

  handleEventResize(info: EventResizeDoneArg): void {
    info.revert();

    const extendedProps = info.event.extendedProps as EventExtendedProps;

    if (
      this.canMakeActionOnCalendar() &&
      this.resManagerService.isDossierEventExtendedProps(extendedProps)
    ) {
      this.handleMoveEvent.emit({
        dossier: extendedProps.dossier,
        startTime: info.event.start,
        endTime: info.event.end,
        timezone: extendedProps.fitter.timezone,
        resizeResource: info.event.getResources()[0]
          ?.extendedProps as ResourceExtendedProps,
      });
    }
  }

  handleEventDrop(info: EventDropArg): void {
    info.revert();

    const eventExtendedProps = info.event.extendedProps as EventExtendedProps;

    if (
      this.canMakeActionOnCalendar() &&
      this.resManagerService.isDossierEventExtendedProps(eventExtendedProps) &&
      eventExtendedProps.dossier.state != DossierStateEnum.CANCELLED &&
      eventExtendedProps.dossier.appointment.status !=
        AppointmentStatusEnum.COMPLETED
    ) {
      const newResourceExtendedProps = info.newResource
        ?.extendedProps as ResourceExtendedProps;
      const oldResourceExtendedProps = info.oldResource
        ?.extendedProps as ResourceExtendedProps;

      this.handleMoveEvent.emit({
        dossier: eventExtendedProps.dossier,
        startTime: info.event.start,
        endTime: info.event.end,
        timezone:
          newResourceExtendedProps?.timezone ||
          eventExtendedProps.fitter.timezone,
        newResource:
          newResourceExtendedProps ||
          (info.event.getResources()[0]
            ?.extendedProps as ResourceExtendedProps),
        oldResource:
          oldResourceExtendedProps ||
          (info.event.getResources()[0]
            ?.extendedProps as ResourceExtendedProps),
      });
    }
  }

  handleEventClick(info: EventClickArg): void {
    info.jsEvent.preventDefault();

    if (info?.jsEvent['pointerType'] === 'touch' || this.isLandscapeView) {
      this.onTooltipClick(info);
      return;
    }

    if (this.canMakeActionOnCalendar()) {
      this.planningTooltipService?._tippy?.unmount();
      this.handleEventClickEvent.emit(info);
    }
  }

  canMakeActionOnCalendar(): boolean {
    return (
      !this.panelsStates.isAppointmentFormOpened &&
      !this.panelsStates.isCustomEventFormOpened
    );
  }

  setExtremitySlotTime(
    slotTimeExtremity: 'slotMaxTime' | 'slotMinTime',
    events: any[],
    currentDay: ConfigType,
    defaultTime: string,
    extremityCompare: (
      eventExtremity: Dayjs,
      baseOfComparison: Dayjs
    ) => boolean,
    formatResultSlotTime: (foundExtremity: Dayjs) => string
  ) {
    const currentDayMoment = dayjs(currentDay).utc(true);
    const defaultTimeMoment = dayjs(defaultTime, 'hh:mm:ss').utc(true);

    const unitOfTimeToCheck: OpUnitType =
      this.calendarMode === 'resourceTimeGridDay' ? 'day' : 'week';
    const startPeriodToCheck = currentDayMoment
      .clone()
      .startOf(unitOfTimeToCheck);
    const endPeriodToCheck = currentDayMoment.clone().endOf(unitOfTimeToCheck);

    // Keep only the events that should be visible on the calendar
    const eventsInPeriod = events.filter(
      (event) =>
        startPeriodToCheck.isBefore(event?.end) &&
        endPeriodToCheck.isAfter(event?.start)
    );

    // Find the event that has the most extreme extremity
    let extremityEventFound = defaultTimeMoment;
    eventsInPeriod.forEach((eventInPeriod) => {
      const eventStart = dayjs(eventInPeriod?.start).utc();
      const eventEnd = dayjs(eventInPeriod?.end).utc();
      if (extremityCompare(eventStart, extremityEventFound)) {
        extremityEventFound = eventStart;
      }
      if (extremityCompare(eventEnd, extremityEventFound)) {
        extremityEventFound = eventEnd;
      }
    });

    // Format the extremity to be able to pass it to fullCalendar options
    const formattedSlotTime = formatResultSlotTime(extremityEventFound);

    // Apply the slot extremity
    this.calendarComponent
      .getApi()
      ?.setOption(slotTimeExtremity, formattedSlotTime);
  }

  setMaxSlotTime(
    events: any[],
    currentDay: ConfigType,
    defaultMaxTime: string
  ) {
    this.setExtremitySlotTime(
      'slotMaxTime',
      events,
      currentDay,
      defaultMaxTime,
      (eventDate, maxEventFound) =>
        eventDate.hour() > maxEventFound.hour() ||
        (eventDate.hour() > maxEventFound.hour() &&
          eventDate.minute() >= maxEventFound.minute()),
      (foundMaxTime) => {
        const defaultMaxTimeMoment = dayjs(defaultMaxTime, 'hh:mm:ss').utc(
          true
        );
        const maxDayHour = foundMaxTime.clone().endOf('day').hour();

        // Add one hour in case the event ends during the XX hour and we cannot see past XX:00:00
        const shouldAddOneHour =
          !foundMaxTime.isSame(defaultMaxTimeMoment) &&
          foundMaxTime.hour() !== maxDayHour;
        const usedMaxSlotTime = foundMaxTime.add(
          shouldAddOneHour ? 1 : 0,
          'hour'
        );

        const isMaxDayHour = usedMaxSlotTime.hour() === maxDayHour;

        // Moment goes to the next day if you add 1 hour to 23hXX so we fix it manually
        const usedFormat = isMaxDayHour
          ? `[${usedMaxSlotTime.hour() + 1}:00:00]`
          : 'HH:mm:ss';
        return usedMaxSlotTime.format(usedFormat);
      }
    );
  }

  setMinSlotTime(
    events: any[],
    currentDay: ConfigType,
    defaultMinTime: string
  ) {
    this.setExtremitySlotTime(
      'slotMinTime',
      events,
      currentDay,
      defaultMinTime,
      (eventDate, minEventFound) => eventDate.hour() <= minEventFound.hour(),
      (foundMinTime) => foundMinTime.format('HH[:00:00]')
    );
  }

  async setSlotTimes() {
    let newDay = this.calendarComponent.getApi()?.getDate();
    if (!newDay) {
      newDay = dayjs().toDate();
    }
    this.changeCalendarDay.emit(newDay);

    /**
     * What we want after setting the min and max slots displayed on calendar is to see the start and
     * the end (not necessarily the middle) of every event present on the displayed time frame
     */

    const displayedFitters = Array.from(
      new Set(
        this._resources
          .map((res) => (res.display ? res.fitterId : null))
          .filter((fId) => !!fId)
      )
    );

    const planningEvents = this.planningSb?.getAgendaEventsSnapshot() || [];
    const planningDossierEvents = planningEvents?.filter(
      (event) =>
        this.resManagerService.isDossierEventExtendedProps(
          event?.extendedProps
        ) && displayedFitters.includes(event?.extendedProps?.fitter?.id)
    );

    const unusedDays =
      this.calendarMode === 'resourceTimeGridWeek'
        ? this.hiddenDays
        : {
            0: true,
            1: true,
            2: true,
            3: true,
            4: true,
            5: true,
            6: true,
            [dayjs(newDay).isoWeekday()]: false,
          };
    this.fitterManagerService
      .getExtremityDefaultSlotTimes(newDay, unusedDays)
      .pipe(
        take(1),
        map((allFittersSlotTimes) =>
          allFittersSlotTimes.filter((fitterSlotTimes) =>
            displayedFitters.includes(fitterSlotTimes.id)
          )
        )
      )
      .subscribe((allFittersSlotTimes) => {
        const startSlotTimes = allFittersSlotTimes
          .map((slotTime) => slotTime.start)
          .sort((slotTime1, slotTime2) => slotTime1.diff(slotTime2, 'seconds'));
        const endSlotTimes = allFittersSlotTimes
          .map((slotTime) => slotTime.end)
          .sort((slotTime1, slotTime2) => slotTime2.diff(slotTime1, 'seconds'));

        this.setMinSlotTime(
          planningDossierEvents,
          newDay,
          startSlotTimes[0]?.format('HH:mm:ss') || this.defaultSlotMinTime
        );
        this.setMaxSlotTime(
          planningDossierEvents,
          newDay,
          endSlotTimes[0]?.format('HH:mm:ss') || this.defaultSlotMaxTime
        );
        this.setNowIndicator();
      });
  }

  async loadEventsInStore() {
    // newDay is current Day of Calendar
    let newDay = this.calendarComponent.getApi()?.getDate();
    if (!newDay) {
      newDay = dayjs().toDate();
    }
    const weekDay = newDay?.getDay();
    const numberOfDays = weekDay - 1;
    // newDay is the Monday of the currentWeek
    newDay.setDate(newDay?.getDate() - numberOfDays);
    // Construct 3 weeks frame
    const begin = dayjs(newDay).add(-7, 'day').format('YYYY-MM-DD');
    const end = dayjs(newDay).add(13, 'day').format('YYYY-MM-DD');
    this.planningSb.updatePlanning(false, true, null, begin, end);
  }

  setNowIndicator() {
    const el = document.querySelector(
      '.now-indicator-revised'
    ) as unknown as HTMLElement;
    if (el) {
      const pxTopValue = parseInt(el.style.top, 10);

      const date = new Date();
      const dateOffsetInHour = date.getTimezoneOffset() / 60;

      const root = document.documentElement;
      root.style.setProperty(
        '--now-indicator-revised',
        `${
          pxTopValue -
          dateOffsetInHour *
            document.querySelector('.fc-timegrid-slot').clientHeight *
            (60 /
              (
                this.calendarComponent.getApi().getOption('slotDuration') as {
                  minute: number;
                }
              ).minute)
        }px`
      );
    }
  }
}
